import React from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Calendar, FileText, FolderKanban, ShieldCheck, CalendarClock, Sparkles } from 'lucide-react';
import { getDayName } from '@/types/timesheetSettings';

interface TimeEntryRulesAccordionProps {
  // Week Configuration
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  onWeekStartDayChange: (day: 0 | 1 | 2 | 3 | 4 | 5 | 6) => void;
  
  // Required Fields
  showRole: boolean;
  requireRole: boolean;
  showCostCentre: boolean;
  requireCostCentre: boolean;
  requireDescription: 'never' | 'always';
  onShowRoleChange: (show: boolean) => void;
  onRequireRoleChange: (require: boolean) => void;
  onShowCostCentreChange: (show: boolean) => void;
  onRequireCostCentreChange: (require: boolean) => void;
  onRequireDescriptionChange: (value: 'never' | 'always') => void;
  
  // Project Selection
  projectSelectionScope: 'all' | 'assigned';
  showArchivedProjects: boolean;
  showProjectTimeRemaining: boolean;
  onProjectSelectionScopeChange: (scope: 'all' | 'assigned') => void;
  onShowArchivedProjectsChange: (show: boolean) => void;
  onShowProjectTimeRemainingChange: (show: boolean) => void;
  
  // Validation Rules
  minimumEntryDuration: '6min' | '15min' | '30min' | '1hour';
  timeRounding: 'none' | '15min' | '30min' | '1hour';
  allowNegativeHours: boolean;
  onMinimumEntryDurationChange: (duration: '6min' | '15min' | '30min' | '1hour') => void;
  onTimeRoundingChange: (rounding: 'none' | '15min' | '30min' | '1hour') => void;
  onAllowNegativeHoursChange: (allow: boolean) => void;
  
  // Date Restrictions
  historicalEntryLimit: number;
  allowFutureDatedEntries: 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited';
  onHistoricalEntryLimitChange: (weeks: number) => void;
  onAllowFutureDatedEntriesChange: (allow: 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited') => void;
  
  // Smart Features
  enableCopyPreviousWeek: boolean;
  copyDescriptions: boolean;
  copyOnlyBillable: boolean;
  enableAutoSmartFill: boolean;
  enableAiInsights: boolean;
  onEnableCopyPreviousWeekChange: (enable: boolean) => void;
  onCopyDescriptionsChange: (copy: boolean) => void;
  onCopyOnlyBillableChange: (copy: boolean) => void;
  onEnableAutoSmartFillChange: (enable: boolean) => void;
  onEnableAiInsightsChange: (enable: boolean) => void;
}

export function TimeEntryRulesAccordion(props: TimeEntryRulesAccordionProps) {
  return (
    <Accordion type="multiple" defaultValue={["basic", "validation", "restrictions", "smart"]} className="space-y-4">
      {/* Basic Settings */}
      <AccordionItem value="basic" className="border rounded-lg px-4">
        <AccordionTrigger className="hover:no-underline">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Calendar className="h-4 w-4 text-primary" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Basic Settings</div>
              <div className="text-sm text-muted-foreground font-normal">
                Week configuration, required fields, and project selection
              </div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="space-y-6 pt-4">
          {/* Week Start Day */}
          <div className="space-y-2">
            <Label htmlFor="weekStartDay" className="text-sm font-medium">
              Week Start Day
            </Label>
            <Select
              value={props.weekStartDay.toString()}
              onValueChange={(value) => props.onWeekStartDayChange(parseInt(value) as 0 | 1 | 2 | 3 | 4 | 5 | 6)}
            >
              <SelectTrigger id="weekStartDay" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Sunday</SelectItem>
                <SelectItem value="1">Monday (Recommended)</SelectItem>
                <SelectItem value="2">Tuesday</SelectItem>
                <SelectItem value="3">Wednesday</SelectItem>
                <SelectItem value="4">Thursday</SelectItem>
                <SelectItem value="5">Friday</SelectItem>
                <SelectItem value="6">Saturday</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Determines the starting day for timesheet weeks, weekly totals, and deadline calculations.
              Currently set to <strong>{getDayName(props.weekStartDay)}</strong>.
            </p>
          </div>

          {/* Required Fields */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <h4 className="text-sm font-semibold">Required Fields</h4>
            </div>
            
            {/* Show Role */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="showRole" className="text-sm font-medium cursor-pointer">
                  Show Role Field
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Display role selector on timesheet entries
                </p>
              </div>
              <div className="flex items-center pt-0.5">
                <Switch
                  id="showRole"
                  checked={props.showRole}
                  onCheckedChange={props.onShowRoleChange}
                />
              </div>
            </div>

            {/* Require Role */}
            <div className="flex items-start justify-between gap-4 pl-4">
              <div className="flex-1">
                <Label 
                  htmlFor="requireRole" 
                  className={`text-sm font-medium cursor-pointer ${!props.showRole ? 'text-muted-foreground' : ''}`}
                >
                  Require Role
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Make role selection mandatory (only if shown)
                </p>
              </div>
              <div className="flex items-center pt-0.5">
                <Switch
                  id="requireRole"
                  checked={props.requireRole}
                  disabled={!props.showRole}
                  onCheckedChange={props.onRequireRoleChange}
                />
              </div>
            </div>

            {/* Show Cost Centre */}
            <div className="flex items-start justify-between gap-4 pt-4 border-t">
              <div className="flex-1">
                <Label htmlFor="showCostCentre" className="text-sm font-medium cursor-pointer">
                  Show Cost Centre Field
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Display cost centre selector on timesheet entries
                </p>
              </div>
              <div className="flex items-center pt-0.5">
                <Switch
                  id="showCostCentre"
                  checked={props.showCostCentre}
                  onCheckedChange={props.onShowCostCentreChange}
                />
              </div>
            </div>

            {/* Require Cost Centre */}
            <div className="flex items-start justify-between gap-4 pl-4">
              <div className="flex-1">
                <Label 
                  htmlFor="requireCostCentre" 
                  className={`text-sm font-medium cursor-pointer ${!props.showCostCentre ? 'text-muted-foreground' : ''}`}
                >
                  Require Cost Centre
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Make cost centre selection mandatory (only if shown)
                </p>
              </div>
              <div className="flex items-center pt-0.5">
                <Switch
                  id="requireCostCentre"
                  checked={props.requireCostCentre}
                  disabled={!props.showCostCentre}
                  onCheckedChange={props.onRequireCostCentreChange}
                />
              </div>
            </div>

            {/* Require Description */}
            <div className="flex items-start justify-between gap-4 pt-4 border-t">
              <div className="flex-1">
                <Label htmlFor="requireDescription" className="text-sm font-medium">
                  Require Description
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  When must entries include a description
                </p>
              </div>
              <div className="flex items-center pt-0.5">
                <Select
                  value={props.requireDescription}
                  onValueChange={props.onRequireDescriptionChange}
                >
                  <SelectTrigger id="requireDescription" className="w-[240px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Never required</SelectItem>
                    <SelectItem value="always">Always required</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Project Selection */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <FolderKanban className="h-4 w-4 text-muted-foreground" />
              <h4 className="text-sm font-semibold">Project Selection</h4>
            </div>

            {/* Project Selection Scope */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-2">
                <Label htmlFor="projectSelectionScope" className="text-sm font-medium">
                  Project Selection Scope
                </Label>
                <p className="text-sm text-muted-foreground">
                  Which projects can employees choose from
                </p>
              </div>
              <div className="w-64">
                <Select
                  value={props.projectSelectionScope}
                  onValueChange={(value) => props.onProjectSelectionScopeChange(value as 'all' | 'assigned')}
                >
                  <SelectTrigger id="projectSelectionScope" className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All active projects</SelectItem>
                    <SelectItem value="assigned">Assigned projects only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Show Archived Projects */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="showArchivedProjects" className="text-sm font-medium cursor-pointer">
                  Show Archived Projects
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Include archived/closed projects in selection list
                </p>
              </div>
              <div className="flex items-center pt-0.5">
                <Switch
                  id="showArchivedProjects"
                  checked={props.showArchivedProjects}
                  onCheckedChange={props.onShowArchivedProjectsChange}
                />
              </div>
            </div>

            {/* Show Project Time Remaining */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="showProjectTimeRemaining" className="text-sm font-medium cursor-pointer">
                  Show Project Time Remaining
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Display budget remaining when selecting projects
                </p>
              </div>
              <div className="flex items-center pt-0.5">
                <Switch
                  id="showProjectTimeRemaining"
                  checked={props.showProjectTimeRemaining}
                  onCheckedChange={props.onShowProjectTimeRemainingChange}
                />
              </div>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>

      {/* Validation Rules */}
      <AccordionItem value="validation" className="border rounded-lg px-4">
        <AccordionTrigger className="hover:no-underline">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Validation Rules</div>
              <div className="text-sm text-muted-foreground font-normal">
                Time entry formatting and validation constraints
              </div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="space-y-4 pt-4">
          {/* Minimum Entry Duration */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label htmlFor="minimumEntryDuration" className="text-sm font-medium">
                Minimum Entry Duration
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Smallest time increment that can be logged
              </p>
            </div>
            <div className="w-[240px]">
              <Select
                value={props.minimumEntryDuration}
                onValueChange={props.onMinimumEntryDurationChange}
              >
                <SelectTrigger id="minimumEntryDuration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6min">6 minutes (0.1 hours)</SelectItem>
                  <SelectItem value="15min">15 minutes (0.25 hours)</SelectItem>
                  <SelectItem value="30min">30 minutes (0.5 hours)</SelectItem>
                  <SelectItem value="1hour">1 hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Time Rounding */}
          <div className="flex items-start justify-between gap-4 pt-4 border-t">
            <div className="flex-1">
              <Label htmlFor="timeRounding" className="text-sm font-medium">
                Time Rounding
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Automatically round time entries to nearest increment
              </p>
            </div>
            <div className="w-[240px]">
              <Select
                value={props.timeRounding}
                onValueChange={props.onTimeRoundingChange}
              >
                <SelectTrigger id="timeRounding">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (no rounding)</SelectItem>
                  <SelectItem value="15min">15 minutes</SelectItem>
                  <SelectItem value="30min">30 minutes</SelectItem>
                  <SelectItem value="1hour">1 hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Allow Negative Hours */}
          <div className="flex items-start justify-between gap-4 pt-4 border-t">
            <div className="flex-1">
              <Label htmlFor="allowNegativeHours" className="text-sm font-medium cursor-pointer">
                Allow Negative Hours
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Permit negative values for corrections/adjustments
              </p>
            </div>
            <div className="flex items-center pt-0.5">
              <Switch
                id="allowNegativeHours"
                checked={props.allowNegativeHours}
                onCheckedChange={props.onAllowNegativeHoursChange}
              />
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>

      {/* Date Restrictions */}
      <AccordionItem value="restrictions" className="border rounded-lg px-4">
        <AccordionTrigger className="hover:no-underline">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <CalendarClock className="h-4 w-4 text-primary" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Date Restrictions</div>
              <div className="text-sm text-muted-foreground font-normal">
                Control how far back or forward time can be logged
              </div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="space-y-4 pt-4">
          {/* Historical Entry Limit */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label htmlFor="historicalEntryLimit" className="text-sm font-medium">
                Historical Entry Limit
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                How many weeks back can time be entered
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                id="historicalEntryLimit"
                type="number"
                min={1}
                max={52}
                value={props.historicalEntryLimit}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (!isNaN(value) && value >= 1 && value <= 52) {
                    props.onHistoricalEntryLimitChange(value);
                  }
                }}
                className="w-20 text-center"
              />
              <span className="text-sm text-muted-foreground">weeks back</span>
            </div>
          </div>

          {/* Allow Future-Dated Entries */}
          <div className="flex items-start justify-between gap-4 pt-4 border-t">
            <div className="flex-1">
              <Label htmlFor="allowFutureDatedEntries" className="text-sm font-medium">
                Allow Future-Dated Entries
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Can employees log time for future dates
              </p>
            </div>
            <div className="w-[240px]">
              <Select
                value={props.allowFutureDatedEntries}
                onValueChange={props.onAllowFutureDatedEntriesChange}
              >
                <SelectTrigger id="allowFutureDatedEntries">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No (current week only)</SelectItem>
                  <SelectItem value="1week">Yes, up to 1 week ahead</SelectItem>
                  <SelectItem value="2weeks">Yes, up to 2 weeks ahead</SelectItem>
                  <SelectItem value="4weeks">Yes, up to 4 weeks ahead</SelectItem>
                  <SelectItem value="unlimited">Yes, unlimited</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>

      {/* Smart Features */}
      <AccordionItem value="smart" className="border rounded-lg px-4">
        <AccordionTrigger className="hover:no-underline">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Smart Features</div>
              <div className="text-sm text-muted-foreground font-normal">
                Intelligent tools to streamline time entry
              </div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="space-y-4 pt-4">
          {/* Copy Previous Week */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label htmlFor="enable-copy-previous-week" className="text-sm font-medium cursor-pointer">
                Enable 'Copy Previous Week'
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Show button to copy entries from previous week
              </p>
            </div>
            <div className="flex items-center pt-0.5">
              <Switch
                id="enable-copy-previous-week"
                checked={props.enableCopyPreviousWeek}
                onCheckedChange={props.onEnableCopyPreviousWeekChange}
              />
            </div>
          </div>

          {/* Copy Descriptions */}
          <div className="flex items-start justify-between gap-4 pt-4 border-t">
            <div className="flex-1">
              <Label htmlFor="copy-descriptions" className="text-sm font-medium cursor-pointer">
                Copy Descriptions
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Include entry descriptions when copying
              </p>
            </div>
            <div className="flex items-center pt-0.5">
              <Switch
                id="copy-descriptions"
                checked={props.copyDescriptions}
                onCheckedChange={props.onCopyDescriptionsChange}
              />
            </div>
          </div>

          {/* Copy Only Billable */}
          <div className="flex items-start justify-between gap-4 pt-4 border-t">
            <div className="flex-1">
              <Label htmlFor="copy-only-billable" className="text-sm font-medium cursor-pointer">
                Copy Only Billable Entries
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                When copying, exclude non-billable time
              </p>
            </div>
            <div className="flex items-center pt-0.5">
              <Switch
                id="copy-only-billable"
                checked={props.copyOnlyBillable}
                onCheckedChange={props.onCopyOnlyBillableChange}
              />
            </div>
          </div>

          {/* Enable Auto Smart Fill */}
          <div className="flex items-start justify-between gap-4 pt-4 border-t">
            <div className="flex-1">
              <Label htmlFor="enable-auto-smart-fill" className="text-sm font-medium cursor-pointer">
                Enable Auto Smart Fill
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Automatically suggest time entries based on patterns
              </p>
            </div>
            <div className="flex items-center pt-0.5">
              <Switch
                id="enable-auto-smart-fill"
                checked={props.enableAutoSmartFill}
                onCheckedChange={props.onEnableAutoSmartFillChange}
              />
            </div>
          </div>

          {/* Enable AI Insights */}
          <div className="flex items-start justify-between gap-4 pt-4 border-t">
            <div className="flex-1">
              <Label htmlFor="enable-ai-insights" className="text-sm font-medium cursor-pointer">
                Enable AI Insights
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Show AI-powered insights and anomaly detection
              </p>
            </div>
            <div className="flex items-center pt-0.5">
              <Switch
                id="enable-ai-insights"
                checked={props.enableAiInsights}
                onCheckedChange={props.onEnableAiInsightsChange}
              />
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
